import React, { useCallback, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { ButtonCircular } from '@deliveryhero/pd-cookbook/components/ButtonCircular';
import { Button } from '@deliveryhero/pd-cookbook/components/ButtonV2';
import Divider from '@deliveryhero/pd-cookbook/components/Divider';
import IcClose from '@deliveryhero/pd-cookbook/components/Icons/IcClose';
import { Typography } from '@deliveryhero/pd-cookbook/components/Typography';
import {
  AppBar,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  Slide,
  Toolbar,
  useTheme
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';

import { AppErrorFallback } from 'components/AppErrorFallback';
import useAction from 'hooks/useAction';
import { logErrorBoundaryError } from 'src/utils/log-error-boundary-error/log-error-boundary-error';
import getConfig from 'utils/get-config/get-config';

import { LabelText } from './constants';
import { ActionProps, ActionType, Props } from './types';

import './styles.scss';

const drawerWidth = 360;
const dialogWidth = 560;

// TODO these colors should be updated to the ones from the theme
// neutral/neutralPrimary
// neutral/neutralSecondary
const neutralPrimary = '#333333';
const neutralSecondary = '#767676';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function Page({
  children,
  actions = {},
  pageOptions = {}
}: Props) {
  const theme = useTheme();
  const { contact_us: contactEmail } = getConfig();
  const [actionsList, setAction, options] = useAction();
  const triggered = Object.keys(actions).reduce<ActionProps[]>((acc, key) => {
    return actionsList.some((_act) => _act === key) && actions[key]
      ? [...acc, actions[key]]
      : acc;
  }, []);

  const actionIsTriggered = useCallback(
    (type: ActionType) => triggered.find((_action) => _action.type === type),
    [triggered]
  );

  const rightDrawer = actionIsTriggered(ActionType.RightDrawer);
  const fullDialog = actionIsTriggered(ActionType.FullDialog);
  const appDialog = actionIsTriggered(ActionType.AppDialog);

  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [fullDialogOpen, setFullDialogOpen] = useState<boolean>(false);
  const [appDialogOpen, setAppDialogOpen] = useState<boolean>(false);

  useEffect(() => {
    actionIsTriggered(ActionType.RightDrawer) && setDrawerOpen(true);
    actionIsTriggered(ActionType.FullDialog) && setFullDialogOpen(true);
    actionIsTriggered(ActionType.AppDialog) && setAppDialogOpen(true);
  }, [actionIsTriggered]);

  const closeRightDrawer = () => handleDrawerClose(ActionType.RightDrawer);
  const closeFullDialog = () => handleDrawerClose(ActionType.FullDialog);
  const closeAppDialog = () => handleDrawerClose(ActionType.AppDialog);

  /**
   * Find the key corresponding to the actions and check if the key exists in actionsList
   * At any point only one key of an ActionType will exist in actionsList
   */
  const handleDrawerClose = (type: ActionType) => {
    const key = Object.keys(actions).find(
      (key) =>
        actions && actions[key].type === type && actionsList.includes(key)
    );
    key && setAction(key, false);
  };

  return (
    <Container disableGutters>
      {children}
      {rightDrawer && rightDrawer.type === ActionType.RightDrawer && (
        <Drawer
          anchor={'right'}
          open={drawerOpen}
          variant="temporary"
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth
            },
            zIndex: 1300
          }}
          onClose={closeRightDrawer}
        >
          <AppBar
            sx={{
              position: 'relative',
              background: 'white',
              color: 'neutral.primary',
              px: theme.spacing(3),
              boxShadow: 'none'
            }}
          >
            <Toolbar
              sx={{ height: 64, justifyContent: 'space-between' }}
              disableGutters
            >
              <Typography type="title-md" color="neutral-primary">
                {rightDrawer.options?.title}
              </Typography>
              <ButtonCircular
                onClick={closeRightDrawer}
                size="small"
                aria-label="close"
              >
                <IcClose />
              </ButtonCircular>
            </Toolbar>
          </AppBar>
          <Container sx={{ px: 3 }} disableGutters>
            <ErrorBoundary
              FallbackComponent={(props) => (
                <AppErrorFallback
                  {...props}
                  title={LabelText.ErrorBoundaryTitle}
                  description={LabelText.ErrorBoundaryDescription}
                  buttonLabel="Reload"
                />
              )}
              onReset={() => {
                window.location.reload();
              }}
              onError={logErrorBoundaryError}
            >
              {rightDrawer.component}
            </ErrorBoundary>
          </Container>
        </Drawer>
      )}
      {fullDialog && fullDialog.type === ActionType.FullDialog && (
        <Dialog
          fullScreen
          open={fullDialogOpen}
          onClose={closeFullDialog}
          TransitionComponent={Transition}
          sx={{
            '& .MuiDialog-paper': {
              background: '#F7F7F7'
            }
          }}
        >
          <AppBar
            sx={{
              position: 'sticky',
              background: 'white',
              color: 'black',
              px: '24px'
            }}
          >
            <Toolbar sx={{ height: 80, justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <ButtonCircular
                  size="small"
                  color="inherit"
                  onClick={closeFullDialog}
                  aria-label="close"
                >
                  <IcClose fill="interaction-primary" />
                </ButtonCircular>
                <Typography
                  type="title-md"
                  color="neutral-primary"
                  marginLeft="sm"
                >
                  {fullDialog.options?.title}
                </Typography>
              </div>
              {fullDialog.options?.action && (
                <Button
                  type={fullDialog.options?.action?.type}
                  onClick={fullDialog.options?.action?.callback}
                  disabled={fullDialog.options?.action?.disabled ?? false}
                  status={
                    fullDialog.options?.action?.loading
                      ? 'is-loading'
                      : 'is-idle'
                  }
                >
                  {fullDialog.options.action.label}
                </Button>
              )}
            </Toolbar>
          </AppBar>
          <div
            style={{
              marginLeft: 'auto',
              marginRight: 'auto',
              padding: '24px 48px',
              width: pageOptions?.fullDialogWidth || '1200px',
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <ErrorBoundary
              FallbackComponent={(props) => (
                <AppErrorFallback
                  {...props}
                  title={LabelText.ErrorBoundaryTitle}
                  description={LabelText.ErrorBoundaryDescription}
                  buttonLabel="Reload page"
                />
              )}
              onReset={() => {
                window.location.reload();
              }}
              onError={logErrorBoundaryError}
            >
              {fullDialog.component}
            </ErrorBoundary>
          </div>
        </Dialog>
      )}
      {appDialog && appDialog.type === ActionType.AppDialog && (
        <Dialog
          open={appDialogOpen}
          onClose={closeAppDialog}
          sx={{
            '& .MuiDialog-paper': {
              width: dialogWidth,
              borderRadius: '16px'
            }
          }}
          fullWidth
        >
          {React.isValidElement(appDialog.options?.title) ? (
            appDialog.options?.title
          ) : (
            <DialogTitle
              id="scroll-dialog-title"
              sx={{
                display: 'flex',
                p: '24px',
                pb: '32px',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Typography type="title-md">
                {appDialog.options?.title}
              </Typography>
              <ButtonCircular
                onClick={closeAppDialog}
                size="small"
                aria-label="close"
              >
                <IcClose fill="brand-pink-80" />
              </ButtonCircular>
            </DialogTitle>
          )}
          <DialogContent sx={{ px: '24px', py: 0 }}>
            <DialogContentText
              sx={{
                fontWeight: 300,
                fontSize: 16,
                letterSpacing: '0.01em',
                alignSelf: 'stretch',
                color: 'neutral.primary',
                whiteSpace: 'pre-line'
              }}
            >
              {appDialog.options?.content}
            </DialogContentText>
          </DialogContent>
          <div className="app-dialog-modal">
            <Divider></Divider>
          </div>
          <DialogActions
            sx={{
              p: '16px 24px 16px 24px',
              alignItems: 'end'
            }}
            onClick={() => handleDrawerClose(ActionType.AppDialog)}
          >
            {appDialog.component}
          </DialogActions>
        </Dialog>
      )}
    </Container>
  );
}
